import { roomFetch } from './roomApi'
import type { BibleAudio, StoryProject } from './types'

export const audioUrl = (assetId: string) => '/api/audio/' + encodeURIComponent(assetId)
export function projectAudio(project: StoryProject) {
  return [...(project.bible?.characters || []), ...(project.bible?.entries || [])].flatMap(item => item.audio || [])
}
export async function uploadAudio(userId: string, file: Blob, signal?: AbortSignal): Promise<Pick<BibleAudio, 'assetId' | 'mimeType' | 'size'>> {
  if (file.size > 25_000_000) throw new Error('Choose an audio file up to 25 MB.')
  const response = await roomFetch(userId, '/api/audio', { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file, signal })
  const data = await response.json()
  if (!response.ok) {
    if (response.status === 401) window.dispatchEvent(new Event('bramble:unauthorized'))
    throw new Error(data.error || 'This recording could not be uploaded.')
  }
  return data
}

export async function audioAssets(project: StoryProject, userId?: string): Promise<Record<string, string>> {
  const assets: Record<string, string> = {}
  for (const recording of projectAudio(project)) {
    if (assets[recording.assetId]) continue
    const response = await fetch(audioUrl(recording.assetId), { credentials: 'same-origin', headers: userId ? { 'X-Bramble-User': userId } : {} })
    if (!response.ok) throw new Error('The backup could not include “' + recording.name + '”. Reconnect and try again, or download a draft with media links.')
    const blob = await response.blob()
    assets[recording.assetId] = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error('The recording could not be read.')); reader.readAsDataURL(blob)
    })
  }
  return assets
}

export async function importAudioAssets(project: StoryProject, assets: unknown, userId: string) {
  const sources = assets && typeof assets === 'object' && !Array.isArray(assets) ? assets as Record<string, unknown> : {}
  const seen = new Set<string>()
  for (const recording of projectAudio(project)) {
    if (seen.has(recording.assetId)) continue
    seen.add(recording.assetId)
    const data = sources[recording.assetId]
    if (typeof data !== 'string') {
      const response = await roomFetch(userId, audioUrl(recording.assetId), { method: 'HEAD' })
      if (!response.ok) throw new Error('This backup has an audio link without its recording: ' + recording.name + '. Export a full backup from the original Bramble room.')
      continue
    }
    if (data.length > 34_000_000 || !/^data:audio\/(mpeg|wav|ogg|mp4|webm|flac);base64,[A-Za-z0-9+/]+={0,2}$/.test(data)) throw new Error('The backup contains invalid audio.')
    const binary = atob(data.slice(data.indexOf(',') + 1)), bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    const digest = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(byte => byte.toString(16).padStart(2, '0')).join('')
    if (digest !== recording.assetId || bytes.length !== recording.size) throw new Error('A recording does not match this backup: ' + recording.name)
    const saved = await uploadAudio(userId, new Blob([bytes], { type: recording.mimeType }))
    if (saved.mimeType !== recording.mimeType) throw new Error('A recording has the wrong file type: ' + recording.name)
  }
}
